import { memo, useContext } from 'react'
import moment from 'moment'
import { Link, Stack, Text } from 'ui/baseComponents'
import StrivePDGuardianLogo from 'assets/img/strivepd-guardian-logo.svg'
import StrivePDPoweredByRuneLogo from 'assets/img/strivepd-poweredby-rune-logo-2.svg'
import { PatientContext } from 'ui/contexts'
import ReportSectionTopConcerns from 'ui/clinicianScreens/Patient/Report/sections/TopConcerns/ReportSectionTopConcerns'
import {
  REPORT_VARIANTS,
  VIDEO_LINKS,
} from 'ui/clinicianScreens/Patient/Report/consts'
import { patientName } from 'utilities/string'
import ReportPeriods from 'ui/clinicianScreens/Patient/Report/components/ReportPeriods'
import { useReportTranslation } from 'utilities/i18n'

export const TROUBLESHOOT_GUIDE_URL =
  'https://s3.us-west-2.amazonaws.com/docs.runelabs.io/static/2024/02/TroubleShooting+and+Best+Practices.pdf'
export const SUPPORT_EMAIL = 'support@runelabs.io'

const ReportHeader = ({
  clinicalVisitId,
  displayMissingDataText,
  isClinicianReport,
  onLoadStart,
  onLoadEnd,
  startDate,
  endDate,
  prevStartDate,
  prevEndDate,
  variant,
}) => {
  const STYLES = {
    headings: {
      marginBottom: '1rem',
    },
    headingClinicianReport: {
      marginTop: '1rem !important', // overriding MUI specificity
    },
    logo: {
      '@media screen': {
        display: 'none',
      },
      '@media print': {
        marginTop: '0 !important', // overriding ridiculous MUI specificity
      },
    },
  }
  const { t, formatDate } = useReportTranslation()
  const patient = useContext(PatientContext)
  const { id: patientId, identifiableInfo } = patient || {}

  // Hardcoding date for Ghost Inspector test to prevent daily screenshot changes
  const isGITest = patientId === 'ghost0'
  const reportDate = isGITest
    ? formatDate(moment('06/26/2024', 'MM/DD/YYYY'))
    : formatDate(moment())

  const { demographicData } = identifiableInfo || {}
  const { dateOfBirth, pdDiagnosisDate } = demographicData || {}
  const name = patientName(patient).firstName()

  const isGuardianReport = variant === REPORT_VARIANTS.guardian

  return (
    <>
      <Stack spacing={0} alignItems="center" sx={STYLES.logo}>
        <img
          alt={
            isGuardianReport
              ? 'StrivePD Guardian'
              : 'StrivePD powered by Rune Labs'
          }
          src={
            isGuardianReport ? StrivePDGuardianLogo : StrivePDPoweredByRuneLogo
          }
          width={150}
        />
      </Stack>

      {isClinicianReport && (
        <Text variant="head32" component="p">
          {t('report.header.namePrefixClinician')}
          {patientName(patient).fullName()}
        </Text>
      )}

      <Stack
        sx={isClinicianReport ? STYLES.headingClinicianReport : STYLES.headings}
        direction="row"
        justifyContent="space-between"
      >
        {isClinicianReport ? (
          <Stack direction="row" alignItems="center" spacing={3}>
            <Text variant="body16B">
              {t('report.header.dateOfBirth')}
              <Text variant="body16" data-cy="dateOfBirth">
                {dateOfBirth
                  ? formatDate(dateOfBirth)
                  : t('report.header.noData')}
              </Text>
            </Text>

            <Text variant="body16B">
              {t('report.header.dateOfDiagnosis')}
              <Text variant="body16" data-cy="dateOfDiagnosis">
                {pdDiagnosisDate
                  ? formatDate(pdDiagnosisDate)
                  : t('report.header.noData')}
              </Text>
            </Text>
          </Stack>
        ) : (
          <Text variant="body16" component="p">
            <Text variant="body16B">
              {t('report.header.namePrefixPatient')}
              {name}! <br />
            </Text>
            {!clinicalVisitId && t('report.header.periodsPrefix')}
          </Text>
        )}
        <Text variant="body16" component="p" data-cy="report-date">
          <Text variant="body16B">{t('report.header.dateOfReport')}</Text>
          {reportDate}
        </Text>
      </Stack>

      {clinicalVisitId && (
        <ReportSectionTopConcerns
          {...{ clinicalVisitId, onLoadStart, onLoadEnd, variant }}
        />
      )}

      <ReportPeriods
        {...{
          clinicalVisitId,
          startDate,
          endDate,
          prevStartDate,
          prevEndDate,
          variant,
        }}
      />

      {!isClinicianReport && displayMissingDataText && (
        <Text
          variant="body16"
          component="p"
          data-cy="highlights-section-no-data"
        >
          {t('report.header.missingDataBefore')}{' '}
          <Link href={VIDEO_LINKS.bestPractices} external underline light>
            {t('report.common.watchOurVideo')}
          </Link>{' '}
          {t('report.header.missingDataMiddle')}{' '}
          <Link href={TROUBLESHOOT_GUIDE_URL} external underline light>
            {t('report.header.missingDataGuide')}
          </Link>
          {t('report.header.missingDataAfterGuide')}{' '}
          <Link href={`mailto:${SUPPORT_EMAIL}`} external underline light>
            {SUPPORT_EMAIL}
          </Link>
          .
        </Text>
      )}
    </>
  )
}

export default memo(ReportHeader)
